import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IAddUserExtraPermissions,
  IRemoveUserExtraPermissions,
  IUserExtraPermissionMutationResponse,
  IUserExtraPermissionsResponse,
} from '@nomoh-monorepo/shared-types';

@Injectable({ providedIn: 'root' })
export class PermissionsStandaloneDataService {
  private http = inject(HttpClient);
  private env = environment;

  // ========================================
  // SIGNALS PRIVADOS
  // ========================================
  private _permissionsData = signal<IUserExtraPermissionsResponse | null>(null);
  private _loading = signal<boolean>(false);

  // ========================================
  // SIGNALS PÚBLICOS (readonly)
  // ========================================
  $permissionsData = this._permissionsData.asReadonly();
  $loading = this._loading.asReadonly();

  // ========================================
  // COMPUTED SIGNALS
  // ========================================
  $planPermissions = computed(
    () => this._permissionsData()?.planPermissions || []
  );
  $extraPermissions = computed(
    () => this._permissionsData()?.extraPermissions || []
  );

  // ========================================
  // MÉTODOS HTTP
  // ========================================

  /**
   * Busca as permissões do usuário (planos + standalone)
   */
  async getUserPermissions(userId: string): Promise<void> {
    this._loading.set(true);
    try {
      const url = `${this.env.apiUrls.nomoh}/pessoa/admin/extra-permissions/${userId}`;
      const response = await this.http
        .get<IUserExtraPermissionsResponse>(url)
        .toPromise();
      if (response) {
        this._permissionsData.set(response);
      }
    } catch (error) {
      console.error('Erro ao buscar permissões do usuário:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Adiciona permissões standalone ao usuário
   */
  async addPermissions(
    dto: IAddUserExtraPermissions
  ): Promise<IUserExtraPermissionMutationResponse | undefined> {
    this._loading.set(true);
    try {
      const url = `${this.env.apiUrls.nomoh}/pessoa/admin/extra-permissions/add`;
      const response = await this.http
        .post<IUserExtraPermissionMutationResponse>(url, dto)
        .toPromise();
      await this.getUserPermissions(dto.userId);
      return response;
    } catch (error) {
      console.error('Erro ao adicionar permissões:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Remove permissões standalone do usuário
   */
  async removePermissions(
    dto: IRemoveUserExtraPermissions
  ): Promise<IUserExtraPermissionMutationResponse | undefined> {
    this._loading.set(true);
    try {
      const url = `${this.env.apiUrls.nomoh}/pessoa/admin/extra-permissions/remove`;
      const response = await this.http
        .request<IUserExtraPermissionMutationResponse>('DELETE', url, {
          body: dto,
        })
        .toPromise();
      await this.getUserPermissions(dto.userId);
      return response;
    } catch (error) {
      console.error('Erro ao remover permissões:', error);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Limpa o estado
   */
  clear(): void {
    this._permissionsData.set(null);
    this._loading.set(false);
  }
}
